/**
 * The main menu.
 *
 * Three ways in: play a mode alone, host it for other people, or join whatever somebody else is
 * hosting. The first two belong to a mode and live on its card, built from what the mode says about
 * itself; the third belongs to nobody and sits below the list. Nothing here knows what a shooting
 * range is, so a new mode reaches the menu by registering and this file never changes.
 *
 * A name comes with the menu, rolled rather than typed, with a dice to roll another. There is no
 * text field here or anywhere else: see `ui/identity/nameRoller.ts`.
 *
 * The save panel is handed in rather than built here. It belongs to whoever holds the profile, and
 * it outlives this screen: the settings screen mounts the same element. The upgrade panel arrives
 * the same way but does not mount here at all: it goes into a sheet, one press away, because a
 * character screen sitting in this column made the menu longer than the game it starts.
 *
 * A notice at the top is how a session that ended says so. Coming back from a game with no
 * explanation is the failure this exists to prevent: a host leaving takes everyone here, and
 * arriving on a menu with no reason reads as a crash. The same banner is how a menu that stayed up
 * through a failure says what went wrong, which is why it can be raised after the screen is built.
 *
 * Every action is a real button: reachable by keyboard, at least 44 pixels tall, and firing on tap
 * without waiting for a click to synthesize.
 *
 * Nothing on this screen explains itself. The wordmark, one band of buttons, the cards, the join
 * row and Settings: a player reads this on every trip back from a game, and a sentence here is a
 * sentence read a hundred times. What a mode is fits on its card's one line and two chips.
 */

import { createJoinEntry } from '@/ui/menu/joinEntry';
import { createNameRoller } from '@/ui/identity/nameRoller';
import { createModeCard } from '@/ui/menu/modeCard';
import type { ModeCard } from '@/ui/menu/modeCard';
import { el } from '@/ui/dom';
import { injectMenuStyles } from '@/ui/menu/menuStyles';
import { SETTINGS_OPEN } from '@/text/settingsText';
import {
  JOIN_TITLE,
  MENU_LEVEL_UP,
  MENU_SECTION,
  MENU_SHEET_CLOSE,
  MENU_WORDMARK,
} from '@/text/menuText';
import { openMenuSheet } from '@/ui/menu/menuSheet';
import type { MenuSheet } from '@/ui/menu/menuSheet';
import type { GameMode } from '@/modes/modeContract';

export interface MenuNotice {
  readonly message: string;
  readonly advice: string;
}

export interface MainMenuHandlers {
  readonly modes: readonly GameMode[];
  readonly onSolo: (mode: GameMode) => void;
  readonly onHost: (mode: GameMode) => void;
  readonly onJoin: (code: string) => void;
  /** What this machine is called, rolled before the menu was built. */
  readonly playerName: string;
  /** A name the player rolled here. The shell keeps it, because the menu comes and goes. */
  readonly onRollName: (name: string) => void;
  /**
   * The save panel, built by the shell and mounted under the name.
   *
   * A slot rather than a pair of callbacks: exporting is one press, importing is a sequence with a
   * confirm in the middle, and the menu owning half of that sequence would put the same state in
   * two files. The settings screen (#33) mounts the same element the same way.
   */
  readonly saveTransfer?: HTMLElement | null;
  /**
   * The upgrade panel, built by the shell and mounted under the save panel.
   *
   * A slot for the same reason and on the same terms. The hub (#28) mounts this element the same
   * way, which is the whole reason it is a component with a root rather than a screen of its own.
   */
  readonly upgrade?: HTMLElement | null;
  /**
   * The settings screen, opened over this one.
   *
   * A callback rather than a slot, unlike the two panels above it: settings is a screen the shell
   * raises over whatever is showing, from here and from a paused game alike, so the menu asks for
   * it rather than holding it.
   */
  readonly onSettings: () => void;
  /** Why the last session ended, when it ended for a reason worth explaining. */
  readonly notice?: MenuNotice | null;
}

export interface MainMenu {
  dispose(): void;
  /**
   * Marks one mode as starting, and freezes the screen until it is cleared with null.
   *
   * The whole menu rather than the pressed card: a mode is being fetched and a session opened, and
   * a second press anywhere on this screen while that runs is a second game starting. `inert` takes
   * the other cards, the roller, the file panel and the join row out of reach in one assignment,
   * which beats teaching four builders a disabled state that lasts a second.
   */
  setBusy(modeId: string | null): void;
  /** A menu that stayed up through a failure has to be able to say what failed. */
  notify(notice: MenuNotice): void;
}

export function createMainMenu(container: HTMLElement, handlers: MainMenuHandlers): MainMenu {
  const removeStyles = injectMenuStyles();
  const roller = createNameRoller({ name: handlers.playerName, onRoll: handlers.onRollName });

  const root = el('div', 'menu');
  // Everything the menu says lives on one measure, centred. The page is the element that carries
  // the width, because the root is fixed to the viewport and cannot cap itself.
  const page = el('div', 'menu-page');
  root.append(page);

  const masthead = el('h1', 'menu-wordmark', MENU_WORDMARK);
  page.append(masthead);

  let banner: HTMLElement | null = null;
  const raise = (notice: MenuNotice): void => {
    banner?.remove();
    banner = sessionNotice(notice);
    masthead.after(banner);
  };

  const notice = handlers.notice ?? null;
  if (notice !== null) raise(notice);

  // Above the modes, because it is true of every way into a game below it and because a name
  // decided after a session has opened is a name that arrives late to the room.
  //
  // One band holds all of it: who you are, the character behind that name, and the file it lives
  // in. Three separately framed panels stacked down the column read as three problems to get past
  // before the game, which is exactly what they are not. No title over it either: the band opens
  // with "You are", which is the title.
  //
  // The name reads across the top and everything you can do about it sits on one row underneath,
  // rerolling included. The dice is moved out of the roller rather than copied: it is the same
  // button, and it belongs with the other three verbs rather than with the noun above them.
  const band = el('div', 'menu-band');
  const bandActions = el('div', 'menu-band-actions');
  bandActions.append(roller.dice);
  band.append(roller.root, bandActions);
  page.append(band);

  // Under the name, because it is about the character that name belongs to, and above the modes,
  // because a player who has just been told nothing is being saved is owed the way out on the same
  // screen rather than one press further in.
  const saveTransfer = handlers.saveTransfer ?? null;

  // A press rather than a panel. The sheet is built on the press and thrown away on the close, and
  // the element inside it is the shell's, so it survives both and comes back holding the same
  // character. Only one can be open, which is what the null check is for.
  let sheet: MenuSheet | null = null;
  const upgrade = handlers.upgrade ?? null;
  if (upgrade !== null) {
    const open = el('button', 'menu-character', MENU_LEVEL_UP);
    open.type = 'button';
    open.addEventListener('click', () => {
      if (sheet !== null) return;
      sheet = openMenuSheet({
        host: root,
        title: MENU_LEVEL_UP,
        closeLabel: MENU_SHEET_CLOSE,
        content: upgrade,
        onClose: (): void => {
          sheet = null;
        },
      });
    });
    bandActions.append(open);
  }

  // After the character button rather than before it: what a character is comes before what to do
  // with the file holding it.
  if (saveTransfer !== null) bandActions.append(saveTransfer);

  const cards = new Map<string, ModeCard>();
  page.append(group(MENU_SECTION, modeList(handlers, cards)));

  const join = createJoinEntry(handlers.onJoin);
  page.append(group(JOIN_TITLE, join.root));

  // At the foot rather than in the band: settings are the game's, not the character's, even though
  // the file they are written to is the character's. Nothing above it moves when a knob is added.
  const settings = el('button', 'menu-settings', SETTINGS_OPEN);
  settings.type = 'button';
  settings.addEventListener('click', handlers.onSettings);
  const utility = el('div', 'menu-utility');
  utility.append(settings);
  page.append(utility);

  container.append(root);

  return {
    dispose(): void {
      sheet?.dispose();
      roller.dispose();
      root.remove();
      removeStyles();
    },

    setBusy(modeId: string | null): void {
      // The sheet is inside the menu, so `inert` reaches it, but a card starting behind a dimmed
      // character screen is a screen nobody can read. It goes down with the press.
      sheet?.dispose();
      for (const [id, card] of cards) card.setState(id === modeId ? 'starting' : 'ready');
      root.inert = modeId !== null;
      root.setAttribute('aria-busy', String(modeId !== null));
    },

    notify: raise,
  };
}

/** A titled box: the mode list and the join row, so the eye gets the same shape twice. */
function group(title: string, ...content: readonly HTMLElement[]): HTMLElement {
  const box = el('div', 'menu-group');
  box.append(el('div', 'menu-section', title), ...content);
  return box;
}

/** What happened, then what to do about it. The wording comes from the failure catalogue. */
function sessionNotice(notice: MenuNotice): HTMLElement {
  const banner = el('div', 'menu-ended');
  banner.setAttribute('role', 'status');
  banner.append(
    el('span', 'menu-ended-message', notice.message),
    el('span', 'menu-ended-advice', notice.advice),
  );
  return banner;
}

function modeList(handlers: MainMenuHandlers, cards: Map<string, ModeCard>): HTMLElement {
  const list = el('ul', 'menu-modes');
  for (const mode of handlers.modes) {
    const item = el('li', '');
    const card = createModeCard(mode, handlers);
    cards.set(mode.id, card);
    item.append(card.root);
    list.append(item);
  }
  return list;
}
